import React, { useMemo, useState } from 'react';
import axios from 'axios';
import { Eye, EyeOff, KeyRound, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { useAuth } from '../../context/AuthContext';
import { MIN_PASSWORD_LENGTH, validatePasswordPair } from '../../utils/passwordValidation';

const rawBackendUrl = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');
const BACKEND_URL = rawBackendUrl.replace(/\/api$/, '');
const API = `${BACKEND_URL}/api`;

const DEFAULT_ENDPOINTS = {
  update: `${API}/agency/settings/password/update`,
  reset: `${API}/agency/settings/password/reset-link`,
};

/**
 * Reusable password management block (matches platform Edit User drawer pattern).
 */
const PasswordManagementSection = ({
  endpoints = DEFAULT_ENDPOINTS,
  className = '',
  'data-testid': dataTestId = 'password-management-section',
}) => {
  const { token } = useAuth();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [updatingPassword, setUpdatingPassword] = useState(false);
  const [sendingReset, setSendingReset] = useState(false);

  const headers = useMemo(
    () => ({ Authorization: `Bearer ${token}` }),
    [token]
  );

  const passwordValidation = useMemo(
    () => validatePasswordPair(newPassword, confirmPassword),
    [newPassword, confirmPassword]
  );

  const clearPasswordFields = () => {
    setNewPassword('');
    setConfirmPassword('');
  };

  const handleUpdatePassword = async () => {
    if (!passwordValidation.valid) return;
    setUpdatingPassword(true);
    try {
      await axios.post(
        endpoints.update,
        { new_password: newPassword },
        { headers, withCredentials: true }
      );
      toast.success('Password updated successfully.');
      clearPasswordFields();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update password');
    } finally {
      setUpdatingPassword(false);
    }
  };

  const handleSendPasswordReset = async () => {
    setSendingReset(true);
    try {
      await axios.post(endpoints.reset, {}, { headers, withCredentials: true });
      toast.success('Password reset link sent.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send password reset link');
    } finally {
      setSendingReset(false);
    }
  };

  return (
    <div className={`space-y-4 ${className}`} data-testid={dataTestId}>
      <div className="flex items-center gap-2">
        <KeyRound className="text-amber-600" size={18} />
        <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">
          Password Management
        </h3>
      </div>
      <p className="text-sm text-slate-500">
        Set a new password or email yourself a secure reset link. Existing passwords are never shown.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <label className="block text-sm font-medium text-slate-700" htmlFor="agency-new-password">
            New Password
          </label>
          <div className="relative">
            <Input
              id="agency-new-password"
              type={showNewPassword ? 'text' : 'password'}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              autoComplete="new-password"
              placeholder={`At least ${MIN_PASSWORD_LENGTH} characters`}
              className="h-11 rounded-xl pr-10 bg-white"
              data-testid="new-password-input"
            />
            <button
              type="button"
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              onClick={() => setShowNewPassword((v) => !v)}
              aria-label={showNewPassword ? 'Hide password' : 'Show password'}
            >
              {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
        </div>

        <div className="space-y-2">
          <label className="block text-sm font-medium text-slate-700" htmlFor="agency-confirm-password">
            Confirm Password
          </label>
          <div className="relative">
            <Input
              id="agency-confirm-password"
              type={showConfirmPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              autoComplete="new-password"
              placeholder="Re-enter new password"
              className="h-11 rounded-xl pr-10 bg-white"
              data-testid="confirm-password-input"
            />
            <button
              type="button"
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              onClick={() => setShowConfirmPassword((v) => !v)}
              aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
            >
              {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
        </div>
      </div>

      {passwordValidation.errors.length > 0 && (newPassword || confirmPassword) && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
          {passwordValidation.errors.map((message) => (
            <p key={message}>{message}</p>
          ))}
        </div>
      )}

      <div className="flex flex-wrap gap-3">
        <Button
          type="button"
          onClick={handleUpdatePassword}
          disabled={!passwordValidation.valid || updatingPassword}
          className="bg-emerald-600 hover:bg-emerald-700"
          data-testid="update-password-btn"
        >
          {updatingPassword ? (
            <>
              <Loader2 size={16} className="mr-2 animate-spin" />
              Updating...
            </>
          ) : (
            'Update Password'
          )}
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={handleSendPasswordReset}
          disabled={sendingReset}
          className="border-slate-200"
          data-testid="send-reset-link-btn"
        >
          {sendingReset ? (
            <>
              <Loader2 size={16} className="mr-2 animate-spin" />
              Sending...
            </>
          ) : (
            'Send Forgot Password Link'
          )}
        </Button>
      </div>
    </div>
  );
};

export default PasswordManagementSection;
